import React from 'react';
import { useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import SkeletonBlock from '@components/common/Skeleton/SkeletonBlock';

import useTranslation from '@hooks/useTranslation';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';
import { useCustomerInfoQuery } from '@services/useCustomerInfoQuery';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import style from './style.module.scss';

interface IProps {
  totalDowntimeAvoided: string | undefined;
  equipmentLaborSaving: string | undefined;
  totalCostAvoided: string | undefined;
  isLoading: boolean;
}

const ArchiveStatistic: React.FC<IProps> = ({
  totalDowntimeAvoided,
  equipmentLaborSaving,
  totalCostAvoided,
  isLoading,
}) => {
  const { t } = useTranslation();
  const { customerId } = useParams();
  const locationSelectValue = selectAppLocationSelectValue();

  const { data: customerInfoData } = useCustomerInfoQuery({
    customerId,
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
  });
  const { data: assetTreeData } = useAssetTreeQuery();

  const isSelectedAllFacility = locationSelectValue.id === 'all';

  const currentCurrency = isSelectedAllFacility
    ? assetTreeData?.customerCurrency
    : locationSelectValue.currency;

  return (
    <div className={style.statisticBlock}>
      <span className={style.statisticTitle}>{t('Total Statistics')}</span>
      {customerInfoData?.showUnplannedDowntime && (
        <div className={style.statisticValueBlock}>
          <span className={style.statisticValue}>
            {isLoading ? <SkeletonBlock className={style.skeletonText} /> : totalDowntimeAvoided || ''}
          </span>
          <span
            className={style.statisticType}
            data-tooltip-content={t('Unplanned Downtime Hours Avoided')}
            data-tooltip-id="statistic-type"
          >
            {t('Unplanned Downtime Hours Avoided')}
          </span>
        </div>
      )}
      <div className={style.statisticValueBlock}>
        <span className={style.statisticValue}>
          {isLoading ? (
            <SkeletonBlock className={style.skeletonText} />
          ) : (
            `${currentCurrency.symbol} ${equipmentLaborSaving || ''}`
          )}
        </span>
        <span
          className={style.statisticType}
          data-tooltip-content={t('Equipment/Labor Savings')}
          data-tooltip-id="statistic-type"
        >
          {t('Equipment/Labor Savings')}
        </span>
      </div>
      {customerInfoData?.showTotalCostSavings && (
        <div className={style.statisticValueBlock}>
          <span className={style.statisticValue}>
            {isLoading ? (
              <SkeletonBlock className={style.skeletonText} />
            ) : (
              `${currentCurrency.symbol} ${totalCostAvoided || ''}`
            )}
          </span>
          <span
            className={style.statisticType}
            data-tooltip-content={t('Total Cost Avoided')}
            data-tooltip-id="statistic-type"
          >
            {t('Total Cost Avoided')}
          </span>
        </div>
      )}
      <Tooltip id="statistic-type" place="top-start" />
    </div>
  );
};

export default ArchiveStatistic;
